<template>
  <div class="ui-form">
    <div class="ui-form__prompt">{{ block.props.prompt }}</div>
    <template v-if="interactive">
      <div v-for="field in block.props.fields" :key="field.key" class="ui-form__field">
        <label :for="`uif-${block.block_id}-${field.key}`" class="ui-field-label">
          {{ field.label }}<span v-if="field.required" class="ui-form__req">required</span>
        </label>
        <textarea
          v-if="field.type === 'textarea'"
          :id="`uif-${block.block_id}-${field.key}`"
          v-model="values[field.key]"
          class="ui-field-input ui-form__textarea"
          :placeholder="field.placeholder || ''"
          rows="3"
        />
        <select
          v-else-if="field.type === 'select'"
          :id="`uif-${block.block_id}-${field.key}`"
          v-model="values[field.key]"
          class="ui-field-input ui-field-select"
        >
          <!--
            Placeholder prompt: shown in the collapsed control while the
            field is empty, but disabled + hidden so it never appears as
            a pickable entry in the open list. Optional fields get an
            explicit "— none —" entry instead as the way back to empty.
          -->
          <option value="" disabled hidden>{{ field.placeholder || '— select —' }}</option>
          <option v-if="!field.required" value="">— none —</option>
          <option v-for="opt in fieldOptions(field)" :key="opt" :value="opt">{{ optionText(field, opt) }}</option>
        </select>
        <UiBlockTreePicker
          v-else-if="field.type === 'tree'"
          :tree="field.tree"
          :initial="field.initial || null"
          :free-text="field.free_text === true"
          :placeholder="field.placeholder || (field.free_text ? 'Enter a value or pick from the list' : '— select —')"
          @pick="values[field.key] = $event"
          @clear="values[field.key] = null"
        />
        <!-- Pills variant wears the shared classes (uiBlockShared.css) so
             form pills and job-search pills stay visually identical. -->
        <div
          v-else-if="field.type === 'radio' || field.type === 'checkbox'"
          :class="field.variant === 'pills' ? 'ui-pills' : 'ui-form__choices'"
        >
          <label
            v-for="opt in fieldOptions(field)"
            :key="opt"
            :class="field.variant === 'pills' ? 'ui-pill-option' : 'ui-form__choice'"
          >
            <input
              :type="field.type"
              :name="`uif-${block.block_id}-${field.key}`"
              :value="opt"
              v-model="values[field.key]"
            />
            <span :class="field.variant === 'pills' ? 'ui-pill' : null">
              {{ opt }}<span v-if="optionHint(field, opt)" class="ui-form__opt-hint"> · {{ optionHint(field, opt) }}</span>
            </span>
          </label>
        </div>
        <input
          v-else
          :id="`uif-${block.block_id}-${field.key}`"
          v-model="values[field.key]"
          type="text"
          class="ui-field-input"
          :placeholder="field.placeholder || ''"
        />
      </div>
      <!-- Engine-emitted placement confirmation: where the new job sits
           among the family's existing roles, in levelling order. -->
      <div v-if="preview" class="ui-form__placement">
        <div class="ui-form__placement-caption">Placement in your architecture</div>
        <div v-if="preview.family_path.length" class="ui-form__placement-family">
          <template v-for="(seg, i) in preview.family_path">
            <span v-if="i" :key="`sep-${i}`" class="ui-form__placement-sep">›</span>
            <span
              :key="`seg-${i}`"
              :class="{ 'ui-form__placement-family-leaf': i === preview.family_path.length - 1 }"
            >{{ seg }}</span>
          </template>
        </div>
        <div ref="placementRowsEl" class="ui-form__placement-rows">
          <div
            v-for="(row, i) in preview.rows"
            :key="i"
            class="ui-form__placement-row"
            :class="{ 'ui-form__placement-row--new': row.is_new }"
          >
            <span class="ui-form__placement-abbr">{{ row.abbr || '—' }}</span>
            <span class="ui-form__placement-title">{{ row.title }}</span>
            <span v-if="row.is_new" class="ui-form__placement-flag">New</span>
          </div>
        </div>
        <div v-if="preview.note" class="ui-form__placement-note">{{ preview.note }}</div>
      </div>
      <div v-if="error" class="ui-form__error">{{ error }}</div>
      <!-- Engine-emitted: why the form is back (a refused submission), or
           the consequence of submitting it as it stands. -->
      <div v-if="block.props.notice" class="ui-form__notice">{{ block.props.notice }}</div>
      <!-- Footer (DS RoleFormCard): quiet way-out on the left, primary
           action on the right. -->
      <div class="ui-form__actions">
        <!-- Engine-emitted way out (the content gate's "Change the
             details") — rides the same workflow-action channel. -->
        <button
          v-if="block.props.secondary_action"
          type="button"
          class="ui-form__secondary-action"
          @click="secondaryClick(block.props.secondary_action)"
        >
          {{ block.props.secondary_action.label }}
        </button>
        <button type="button" class="ui-primary-btn ui-form__submit" @click="submit">
          {{ block.props.submit_label || 'Submit' }}
        </button>
      </div>
    </template>
    <div v-else class="ui-form__inert">This step is no longer active.</div>
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, onMounted, reactive, ref, watch } from 'vue';
import UiBlockTreePicker from './UiBlockTreePicker.vue';

/**
 * `form` ui_block — multi-field capture inside the chat. Submitting
 * emits `action` with `{ key: 'submitted', label, value }` where value
 * is the field map (object) — the renderer encodes it as `value_json=`
 * on the [workflow:action] marker, and the engine records it under
 * `values[step_key]`.
 *
 * NB `values` is seeded with EVERY field key up front — Vue 2.7's
 * reactive() can't detect key additions (see project memory), so
 * v-model targets must exist from the start.
 */
export default defineComponent({
  name: 'UiBlockForm',
  components: { UiBlockTreePicker },
  props: {
    block: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
  },
  emits: ['action'],
  setup(props, { emit }) {
    // Engine-emitted placement confirmation payload (parser-validated).
    const preview = computed(() => props.block.props.placement_preview || null);
    // Checkbox groups are MULTI-select: v-model needs an array target,
    // and the submitted value for them is the array of chosen options.
    // Tree fields hold an OBJECT { value, node_id, path } once picked
    // (the shared UiBlockTreePicker emits that payload).
    // Engine-emitted `initial` pre-fills a field (e.g. the doc_upload
    // hierarchy confirmation seeds pickers with the chain-identified
    // placement) — the user keeps or changes it before submitting.
    const seed = {};
    for (const field of props.block.props.fields) {
      if (field.type === 'checkbox') {
        seed[field.key] = Array.isArray(field.initial) ? [...field.initial] : [];
      } else if (field.type === 'tree') {
        seed[field.key] = field.initial && typeof field.initial === 'object' ? field.initial : null;
      } else {
        seed[field.key] = typeof field.initial === 'string' ? field.initial : '';
      }
    }
    const values = reactive(seed);
    const error = ref('');

    // ── Levelling cascade ────────────────────────────────────────────
    // Engine-emitted `props.levelling` (spec-validated): field keys for
    // the level / track / abbreviation roles plus the framework's valid
    // combinations. A role field's options narrow to the combos that
    // agree with values already picked in EARLIER role fields (form
    // order) — pick "Level 3" and the track list only shows tracks that
    // offer level 3. Earlier fields are never narrowed by later picks.
    const lf = computed(() => {
      const spec = props.block.props.levelling;
      if (!spec || !Array.isArray(spec.combos) || !spec.combos.length || !spec.keys) return null;
      const roleByKey = {};
      for (const [role, key] of Object.entries(spec.keys)) {
        if (typeof key === 'string' && key) roleByKey[key] = role;
      }
      return Object.keys(roleByKey).length >= 2 ? { combos: spec.combos, roleByKey } : null;
    });

    const norm = (s) => `${s ?? ''}`.trim().toLowerCase();

    /** Selections held by the OTHER cascade fields. `earlierOnly` limits
     * to fields ABOVE `key` in form order — option NARROWING is
     * directional (that's what makes it predictable); option HINTS use
     * every other field's pick regardless of position. */
    function roleSelections(key, earlierOnly) {
      const out = [];
      for (const f of props.block.props.fields) {
        if (f.key === key) {
          if (earlierOnly) break;
          continue;
        }
        const r = lf.value.roleByKey[f.key];
        if (!r) continue;
        // checkbox role fields hold arrays — any selected value counts.
        const raw = values[f.key];
        const sel = (Array.isArray(raw) ? raw : [raw]).map(norm).filter(Boolean);
        if (sel.length) out.push({ role: r, sel });
      }
      return out;
    }

    /** The options a field offers RIGHT NOW — the authored list, narrowed
     * by the levelling cascade when the field plays a cascade role. */
    function fieldOptions(field) {
      const base = field.options || [];
      if (!lf.value) return base;
      const role = lf.value.roleByKey[field.key];
      if (!role) return base;
      const constraints = roleSelections(field.key, true);
      if (!constraints.length) return base;
      const allowed = new Set();
      for (const combo of lf.value.combos) {
        if (constraints.every((c) => c.sel.includes(norm(combo[c.role])))) {
          allowed.add(norm(combo[role]));
        }
      }
      return base.filter((opt) => allowed.has(norm(opt)));
    }

    /** What picking this option IMPLIES for the other interlinked
     * framework roles, when uniquely determined — the level list shows
     * "3 — IC3" once a track is picked, an abbreviation list shows its
     * level and track outright. Roles the user already picked exactly
     * are omitted (they'd repeat on every line); a role that stays
     * ambiguous (several combos) shows nothing rather than guessing. */
    function optionHint(field, opt) {
      if (!lf.value) return '';
      const role = lf.value.roleByKey[field.key];
      if (!role) return '';
      const constraints = roleSelections(field.key, false);
      const matches = lf.value.combos.filter(
        (c) =>
          norm(c[role]) === norm(opt) &&
          constraints.every((k) => k.sel.includes(norm(c[k.role]))),
      );
      if (!matches.length) return '';
      const parts = [];
      for (const other of ['level', 'track', 'abbreviation']) {
        if (other === role) continue;
        const picked = constraints.find((k) => k.role === other);
        if (picked && picked.sel.length === 1) continue;
        const vals = [...new Set(matches.map((c) => `${c[other] ?? ''}`.trim()).filter(Boolean))];
        if (vals.length === 1) parts.push(vals[0]);
      }
      return parts.join(' · ');
    }

    /** Option display text for the native select — value stays the raw
     * option; only the visible line carries the hint. */
    function optionText(field, opt) {
      const hint = optionHint(field, opt);
      return hint ? `${opt} — ${hint}` : opt;
    }

    // A pick in an earlier cascade field can invalidate what a later one
    // already holds — drop the now-impossible value rather than submit a
    // combination the framework doesn't have.
    watch(values, () => {
      if (!lf.value) return;
      for (const field of props.block.props.fields) {
        if (!lf.value.roleByKey[field.key]) continue;
        const open = fieldOptions(field).map(norm);
        const raw = values[field.key];
        if (Array.isArray(raw)) {
          const kept = raw.filter((v) => open.includes(norm(v)));
          if (kept.length !== raw.length) values[field.key] = kept;
        } else if (typeof raw === 'string' && raw.trim() && !open.includes(norm(raw))) {
          values[field.key] = '';
        }
      }
    });

    function isEmpty(v) {
      if (Array.isArray(v)) return v.length === 0;
      if (v && typeof v === 'object') return false;
      return !`${v ?? ''}`.trim();
    }

    function submit() {
      const missing = props.block.props.fields.filter(
        (f) => f.required && isEmpty(values[f.key]),
      );
      if (missing.length) {
        error.value = `Please fill in: ${missing.map((f) => f.label).join(', ')}`;
        return;
      }
      error.value = '';
      const payload = {};
      const summary = [];
      for (const field of props.block.props.fields) {
        const raw = values[field.key];
        if (Array.isArray(raw)) {
          if (raw.length) {
            payload[field.key] = raw;
            summary.push(`${field.label}: ${raw.join(', ')}`);
          }
        } else if (raw && typeof raw === 'object') {
          // Tree pick: { value, node_id?, path } — summarise by path.
          payload[field.key] = raw;
          summary.push(
            `${field.label}: ${Array.isArray(raw.path) ? raw.path.join(' / ') : raw.value}`,
          );
        } else {
          const v = `${raw ?? ''}`.trim();
          if (v) {
            payload[field.key] = v;
            summary.push(`${field.label}: ${v}`);
          }
        }
      }
      emit('action', {
        key: 'submitted',
        // A fields-less decision card (content gate, placement
        // confirmation) has nothing to summarise — the button label IS
        // the answer.
        label: summary.length
          ? `I provided:\n${summary.join('\n')}`
          : props.block.props.submit_label || 'Submit',
        value: payload,
      });
    }

    /**
     * Engine-emitted secondary action (the content gate's "Change the
     * details"). A COMMAND, not an answer: the label goes in the bubble
     * verbatim and the ready-made value payload rides along.
     */
    function secondaryClick(action) {
      if (!props.interactive || !action) return;
      emit('action', { key: action.action, label: action.label, value: action.value });
    }

    // The placement list scrolls, and the NEW row slots in framework
    // order — often below the fold, where nothing says it exists (user
    // report, 2026-08-27). Centre it in the LIST's own scroll only:
    // scrollIntoView would also scroll the chat viewport, yanking the
    // conversation every time history re-renders an old card.
    const placementRowsEl = ref(null);
    onMounted(() => {
      void nextTick(() => {
        const container = placementRowsEl.value;
        const row = container?.querySelector?.('.ui-form__placement-row--new');
        if (!container || !row) return;
        const top = row.offsetTop - container.offsetTop;
        container.scrollTop = Math.max(
          0,
          top - (container.clientHeight - row.clientHeight) / 2,
        );
      });
    });

    return {
      values,
      error,
      preview,
      fieldOptions,
      optionHint,
      optionText,
      submit,
      secondaryClick,
      placementRowsEl,
    };
  },
});
</script>

<style scoped>
/* Prototype Surface treatment: a quiet white card — hairline grey
   border, 16px radius, whisper of a shadow — instead of indigo-tinted
   chrome. The indigo stays on the actions. */
.ui-form {
  margin-top: 12px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(229, 231, 235, 0.9);
  background: white;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
/* Footer row (DS RoleFormCard): quiet action left, primary right. */
.ui-form__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
}
.ui-form__actions .ui-form__submit {
  margin-left: auto;
}
/* Quiet way-out (DS QuietBtn): bare text that darkens on hover. */
.ui-form__secondary-action {
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: rgb(107 114 128);
  cursor: pointer;
  transition: color 0.15s ease;
}
.ui-form__secondary-action:hover {
  color: rgb(17 24 39);
}
/* ── Placement confirmation panel ─────────────────────────────── */
.ui-form__placement {
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgb(226 232 240);
  background: rgba(255, 255, 255, 0.75);
}
.ui-form__placement-caption {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgb(148 163 184);
  margin-bottom: 4px;
}
.ui-form__placement-family {
  font-size: 12px;
  color: rgb(71 85 105);
  margin-bottom: 8px;
}
.ui-form__placement-family-leaf {
  font-weight: 600;
  color: rgb(30 41 59);
}
.ui-form__placement-sep {
  margin: 0 5px;
  color: rgb(148 163 184);
}
.ui-form__placement-rows {
  max-height: 260px;
  overflow-y: auto;
}
.ui-form__placement-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 12.5px;
  color: rgb(31 41 55);
}
/* The new job's row — the one decision on the card. */
.ui-form__placement-row--new {
  background: rgb(238 242 255);
  border-left: 3px solid rgb(79 70 229);
  font-weight: 600;
}
.ui-form__placement-abbr {
  flex: 0 0 34px;
  font-size: 11px;
  font-weight: 600;
  color: rgb(100 116 139);
}
.ui-form__placement-row--new .ui-form__placement-abbr {
  color: rgb(67 56 202);
}
.ui-form__placement-title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ui-form__placement-flag {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  color: rgb(67 56 202);
}
.ui-form__placement-note {
  margin-top: 6px;
  font-size: 10.5px;
  color: rgb(148 163 184);
}

/* A refusal or a consequence, not a hint — the prototype's quiet amber
   note: soft wash with an amber left tick, readable grey text. */
.ui-form__notice {
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border-left: 2px solid rgb(245 158 11);
  background: rgb(255 251 235);
  font-size: 11.5px;
  line-height: 1.45;
  color: rgb(75 85 99);
}
/* Card heading (DS Eyebrow): small, semibold, quiet grey. */
.ui-form__prompt {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: rgb(156 163 175);
  margin-bottom: 12px;
}
.ui-form__field {
  margin-bottom: 16px;
}
/* "required" tag beside the label (DS RoleFormCard), not a red star. */
.ui-form__req {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: rgb(99 102 241);
}
/* Sizing only — the input look comes from .ui-field-input (shared). */
.ui-form__textarea {
  resize: vertical;
}
.ui-form__choices {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
}
.ui-form__choice {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: rgb(31 41 55);
  cursor: pointer;
}
/* Interlinked framework value implied by the option (levelling cascade)
   — quiet, the option value itself stays the emphasis. */
.ui-form__opt-hint {
  font-size: 11px;
  color: rgb(148 163 184);
}
.ui-form__error {
  margin-bottom: 8px;
  font-size: 12px;
  color: rgb(185 28 28);
}
.ui-form__inert {
  font-size: 12px;
  color: rgb(107 114 128);
}
</style>

<!-- Shared pill/button look — edit uiBlockShared.css, not the scoped block above. -->
<style src="./uiBlockShared.css"></style>
